import classNames from "classnames";
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { LuChevronLeft, LuX } from "react-icons/lu";
import { Link } from "react-router-dom";

import { getInitialsFromFullName } from "../../../constants/constants";
import { isPastDue } from "./invoiceStatus.utils";

// Shared look for the Billing pages (owner's V49 design).

export const BILLING_PRIMARY_BUTTON =
  "inline-flex h-10 items-center justify-center gap-2 rounded-[10px] bg-[#6B4EFF] px-[18px] text-[13.5px] font-bold text-white shadow-[0_3px_10px_rgba(107,78,255,0.28)] transition-colors hover:bg-[#5A3EEB] disabled:cursor-not-allowed disabled:bg-[#C3C8D4] disabled:shadow-none";

export const BILLING_SECONDARY_BUTTON =
  "inline-flex h-10 items-center justify-center gap-2 rounded-[9px] border border-[#DFE2E8] bg-white px-5 text-[13.5px] font-semibold text-[#3A4256] transition-colors hover:bg-[#F7F8FA] disabled:cursor-not-allowed disabled:opacity-60";

export const BILLING_INPUT =
  "h-10 w-full rounded-[9px] border border-[#DFE2E8] bg-white px-3 text-[13.5px] text-[#1F2433] outline-none placeholder:text-[#9AA0B0] focus:border-[#6B4EFF]";

export const BILLING_FIELD_LABEL = "text-xs font-bold text-[#9097A8]";

export const BILLING_CARD =
  "rounded-2xl border border-[#E8E8EC] bg-white shadow-[0_1px_2px_rgba(20,22,34,0.04)]";

export const formatMoney = (value?: number | null) =>
  `$${(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

type HeaderProps = {
  activeTab: "invoices" | "payments";
  onBack?: () => void;
  onClose?: () => void;
  onTabClick: (tab: "invoices" | "payments") => void;
};

/** White band with the page title and the Invoices / Payments tabs. */
export const BillingHeader: FC<HeaderProps> = ({
  activeTab,
  onBack,
  onClose,
  onTabClick,
}) => (
  <div
    className={classNames(
      "border-b border-[#ECEEF2] bg-white px-8 max-md:px-4",
      onBack ? "pt-5" : "pt-[22px]",
    )}>
    <div className={"mb-3.5 flex items-center gap-3"}>
      {onBack && (
        <button
          type={"button"}
          aria-label={"Back"}
          onClick={onBack}
          className={
            "flex h-8 w-8 items-center justify-center rounded-lg border border-[#E6E8ED] text-[#6B7185] hover:bg-[#F4F2FF]"
          }>
          <LuChevronLeft size={17} />
        </button>
      )}
      <h1
        className={classNames(
          "m-0 font-extrabold tracking-[-0.5px] text-[#1F2433]",
          onBack ? "text-2xl" : "text-[26px]",
        )}>
        Billing
      </h1>
      {onClose && (
        <button
          type={"button"}
          aria-label={"Close"}
          title={"Close"}
          onClick={onClose}
          className={
            "ml-auto flex h-8 w-8 items-center justify-center rounded-lg border border-[#E6E8ED] text-[#6B7185] hover:bg-[#F4F2FF] hover:text-[#1F2433]"
          }>
          <LuX size={16} />
        </button>
      )}
    </div>
    <div className={"flex gap-[26px]"}>
      {(["invoices", "payments"] as const).map((tab) => (
        <button
          key={tab}
          type={"button"}
          onClick={() => onTabClick(tab)}
          className={classNames(
            "-mb-px border-b-2 px-0.5 pb-3 text-sm capitalize",
            activeTab === tab
              ? "border-[#6B4EFF] font-bold text-[#6B4EFF]"
              : "border-transparent font-semibold text-[#6B7185] hover:text-[#3A4256]",
          )}>
          {tab}
        </button>
      ))}
    </div>
  </div>
);

/** `hint` = what the number counts, shown on hover. */
export const StatCard: FC<{ label: string; value: string; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <div
    title={hint}
    className={
      "rounded-[14px] border border-[#E8E8EC] bg-white px-6 py-[26px] text-center shadow-[0_1px_2px_rgba(20,22,34,0.04)] max-md:px-3 max-md:py-4"
    }>
    <div
      className={
        "mb-3 text-[15px] text-[#6B7185] max-md:mb-1 max-md:text-[13px]"
      }>
      {label}
    </div>
    <div
      className={
        "text-[26px] font-extrabold tracking-[-0.5px] text-[#1F2433] max-md:break-words max-md:text-[19px]"
      }>
      {value}
    </div>
  </div>
);

export const FilterField: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <div className={"flex flex-col gap-1.5"}>
    <span className={"text-[12.5px] font-bold text-[#3A4256]"}>{label}</span>
    {children}
  </div>
);

export type BillingClient = {
  id?: string;
  name: string;
  isOrganization?: boolean;
  /** Client page; the name becomes a link (QA batch 12). */
  link?: string | null;
};

export const clientAvatarColor = (isOrganization?: boolean) =>
  isOrganization ? "#6938EF" : "#1B3E9F";

export const ClientAvatar: FC<{
  client: BillingClient;
  size?: number;
  ring?: boolean;
  className?: string;
  style?: React.CSSProperties;
}> = ({ client, size = 28, ring, className, style }) => (
  <span
    className={classNames(
      "flex flex-none items-center justify-center rounded-full font-bold text-white",
      ring && "border-2 border-white",
      className,
    )}
    style={{
      width: size,
      height: size,
      fontSize: size >= 28 ? 11 : size >= 24 ? 10 : 9,
      backgroundColor: clientAvatarColor(client.isOrganization),
      ...style,
    }}>
    {getInitialsFromFullName(client.name || "?").toUpperCase()}
  </span>
);

const ClientList: FC<{ clients: BillingClient[] }> = ({ clients }) => (
  <>
    {clients.map((client, index) => (
      <span
        key={client.id || `${client.name}-${index}`}
        className={"flex items-center gap-2.5 rounded-lg px-2.5 py-2"}>
        <ClientAvatar client={client} size={26} />
        <span
          className={
            "whitespace-nowrap text-[13.5px] font-semibold text-[#1F2433]"
          }>
          {client.name}
        </span>
      </span>
    ))}
  </>
);

/** Invoice list: first client, then a "+2 more" chip that opens the full list. */
export const ClientsCell: FC<{
  clients: BillingClient[];
  /** How many more clients there are, when the names aren't all known. */
  moreCount?: number;
}> = ({ clients, moreCount = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const [first, ...rest] = clients;
  const extra = Math.max(rest.length, moreCount);

  useEffect(() => {
    if (!isOpen) return;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen]);

  if (!first) return null;

  return (
    <span ref={ref} className={"relative flex min-w-0 items-center gap-2.5"}>
      <ClientAvatar client={first} />
      {first.link ? (
        <Link
          to={first.link}
          onClick={(event) => event.stopPropagation()}
          className={
            "truncate text-[13.5px] font-semibold text-[#1F2433] hover:text-[#6B4EFF] hover:underline"
          }>
          {first.name}
        </Link>
      ) : (
        <span className={"truncate text-[13.5px] font-semibold text-[#1F2433]"}>
          {first.name}
        </span>
      )}
      {extra > 0 && (
        <button
          type={"button"}
          onClick={(event) => {
            event.stopPropagation();
            setIsOpen((open) => !open);
          }}
          className={
            "flex-none whitespace-nowrap rounded-[14px] border border-[#E1E0F5] bg-white px-2.5 py-[3px] text-xs font-semibold text-[#6B4EFF] hover:bg-[#F4F2FF]"
          }>
          +{extra} more
        </button>
      )}
      {isOpen && (
        <div
          onClick={(event) => event.stopPropagation()}
          className={
            "absolute left-0 top-[calc(100%+6px)] z-30 min-w-[220px] cursor-default rounded-[10px] border border-[#E8E8EC] bg-white p-1.5 shadow-[0_14px_36px_rgba(20,22,34,0.18)]"
          }>
          <ClientList clients={clients} />
          {extra > rest.length && (
            <div className={"px-2.5 py-1.5 text-xs text-[#8B92A2]"}>
              and {extra - rest.length} more
            </div>
          )}
        </div>
      )}
    </span>
  );
};

/**
 * Invoice page: one client is shown as a chip; several are shown as
 * overlapping avatars with "N clients", and hovering lists them.
 */
export const InvoiceClients: FC<{
  clients: BillingClient[];
  onClientClick?: (client: BillingClient) => void;
}> = ({ clients, onClientClick }) => {
  if (clients.length <= 1) {
    return (
      <div className={"flex flex-wrap items-center gap-2.5"}>
        {clients.map((client, index) => (
          <button
            type={"button"}
            key={client.id || `${client.name}-${index}`}
            onClick={() => onClientClick?.(client)}
            className={
              "inline-flex items-center gap-[9px] rounded-[22px] border border-[#ECEAF4] bg-[#F6F5FB] py-[5px] pl-1.5 pr-[13px] hover:border-[#D8D0FF]"
            }>
            <ClientAvatar client={client} />
            <span className={"text-sm font-semibold text-[#1F2433]"}>
              {client.name}
            </span>
          </button>
        ))}
      </div>
    );
  }

  const shown = clients.slice(0, 5);
  const extra = clients.length - shown.length;

  return (
    <div className={"group relative inline-flex cursor-default items-center"}>
      {shown.map((client, index) => (
        <ClientAvatar
          key={client.id || `${client.name}-${index}`}
          client={client}
          size={30}
          ring
          style={{
            marginLeft: index === 0 ? 0 : -9,
            zIndex: 10 - index,
            position: "relative",
          }}
        />
      ))}
      {extra > 0 && (
        <span
          className={
            "relative z-[1] -ml-[9px] flex h-[30px] w-[30px] flex-none items-center justify-center rounded-full border-2 border-white bg-[#EDEBF6] text-[11px] font-bold text-[#5B6170]"
          }>
          +{extra}
        </span>
      )}
      <span className={"ml-3 text-[13px] font-semibold text-[#6B7185]"}>
        {clients.length} clients
      </span>
      <div
        className={
          "invisible absolute left-0 top-full z-30 pt-2 opacity-0 transition-opacity group-hover:visible group-hover:opacity-100"
        }>
        <div
          className={
            "flex max-h-[280px] min-w-[230px] flex-col gap-0.5 overflow-y-auto rounded-[11px] border border-[#E8E8EC] bg-white p-[7px] shadow-[0_14px_36px_rgba(20,22,34,0.16)]"
          }>
          {clients.map((client, index) => (
            <button
              type={"button"}
              key={client.id || `${client.name}-${index}`}
              onClick={() => onClientClick?.(client)}
              className={
                "flex items-center gap-2.5 rounded-lg px-[9px] py-[7px] text-left hover:bg-[#F4F2FF]"
              }>
              <ClientAvatar client={client} size={26} />
              <span className={"text-[13.5px] font-semibold text-[#1F2433]"}>
                {client.name}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/** Status text colours from the design. */
export const invoiceStatusColor = (status?: string | null) => {
  switch (status) {
    case "Paid":
      return "#15803D";
    case "Sent":
      return "#6B4EFF";
    case "Draft":
      return "#9097A8";
    default:
      return "#6B7185";
  }
};

export const invoiceStatusBadgeBg = (status?: string | null) =>
  status === "Paid" ? "#E7F7EE" : status === "OverDue" ? "#FDECEC" : "#F0F1F4";

export const PaidIcon: FC = () => (
  <svg width={16} height={16} viewBox={"0 0 24 24"} aria-hidden={true}>
    <circle cx={12} cy={12} r={10} fill={"#16A34A"} />
    <path
      d={"m8 12 3 3 5-6"}
      fill={"none"}
      stroke={"#fff"}
      strokeWidth={2}
      strokeLinecap={"round"}
      strokeLinejoin={"round"}
    />
  </svg>
);

/** Square checkbox used in the billing tables and pickers. */
export const BillingCheckbox: FC<{
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (checked: boolean) => void;
  size?: number;
}> = ({ checked, disabled, label, onChange, size = 20 }) => (
  <button
    type={"button"}
    role={"checkbox"}
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={(event) => {
      event.stopPropagation();
      onChange(!checked);
    }}
    style={{ width: size, height: size }}
    className={classNames(
      "inline-flex flex-none items-center justify-center rounded-[5px] border-2 transition-colors",
      checked
        ? "border-[#6B4EFF] bg-[#6B4EFF]"
        : "border-[#C9CED8] bg-white hover:border-[#6B4EFF]",
      disabled && "cursor-not-allowed opacity-50",
    )}>
    {checked && (
      <svg
        width={size * 0.6}
        height={size * 0.6}
        viewBox={"0 0 24 24"}
        fill={"none"}
        stroke={"#fff"}
        strokeWidth={3.4}
        strokeLinecap={"round"}
        strokeLinejoin={"round"}>
        <path d={"M20 6 9 17l-5-5"} />
      </svg>
    )}
  </button>
);

export type BillingMenuItem = {
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  onClick: () => void;
};

/** "⋮" button with a small menu (Edit / Pay / Delete). */
const MENU_WIDTH = 150;

export const ActionsMenu: FC<{ items: BillingMenuItem[]; size?: number }> = ({
  items,
  size = 34,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // The menu floats above the page (portal, fixed position): inside tables
  // with their own scroll box it used to be cut off (Roadmap 1843).
  const [position, setPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
  } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const left = Math.max(8, rect.right - MENU_WIDTH);
    const below = window.innerHeight - rect.bottom;
    const needed = items.length * 40 + 20;
    setPosition(
      below < needed && rect.top > below
        ? { bottom: window.innerHeight - rect.top + 6, left }
        : { top: rect.bottom + 6, left },
    );
  }, [items.length]);

  useEffect(() => {
    if (!isOpen) return;
    place();
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !ref.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    const shut = () => setIsOpen(false);
    document.addEventListener("mousedown", close);
    window.addEventListener("resize", shut);
    window.addEventListener("scroll", shut, true);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("resize", shut);
      window.removeEventListener("scroll", shut, true);
    };
  }, [isOpen, place]);

  return (
    <div ref={ref} className={"relative"}>
      <button
        type={"button"}
        aria-label={"More actions"}
        aria-expanded={isOpen}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((open) => !open);
        }}
        style={{ width: size, height: size }}
        className={
          "flex items-center justify-center rounded-lg border border-[#E6E8ED] text-[#6B7185] hover:bg-[#F4F2FF]"
        }>
        <svg width={17} height={17} viewBox={"0 0 24 24"} fill={"currentColor"}>
          <circle cx={12} cy={5} r={1.6} />
          <circle cx={12} cy={12} r={1.6} />
          <circle cx={12} cy={19} r={1.6} />
        </svg>
      </button>
      {isOpen &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            role={"menu"}
            style={{ ...position, width: MENU_WIDTH }}
            className={
              "fixed z-[60] flex flex-col rounded-[11px] border border-[#ECEEF2] bg-white p-1.5 shadow-[0_14px_36px_rgba(20,22,34,0.2)]"
            }>
            {items.map((item) => (
              <button
                key={item.label}
                type={"button"}
                role={"menuitem"}
                onClick={(event) => {
                  event.stopPropagation();
                  setIsOpen(false);
                  item.onClick();
                }}
                className={classNames(
                  "flex items-center gap-[11px] rounded-lg px-[11px] py-[9px] text-left text-[13.5px] font-semibold",
                  item.danger
                    ? "text-[#E5484D] hover:bg-[#FDECEC]"
                    : "text-[#3A4256] hover:bg-[#F4F2FF]",
                )}>
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

/** Paid / Partially Paid / OverDue / Not due yet, from the invoice amounts. */
export const deriveInvoiceStatus = (invoice: {
  status?: string | null;
  total?: number | null;
  dueDate?: string | null;
  isPayed?: boolean | null;
  balanceDue: number;
}) => {
  // Nothing left to pay ($0 invoice or overpaid) = Paid, never "OverDue" (QA C4).
  if (invoice.isPayed) return "Paid";
  if (invoice.total != null && invoice.balanceDue <= 0.004) return "Paid";
  if (invoice.status) return invoice.status;
  const total = invoice.total || 0;
  if (!total) return null;
  if (invoice.balanceDue < total) return "Partially Paid";
  // Due today = not overdue yet, like the invoice list (QA batch 12).
  if (isPastDue(invoice.dueDate)) {
    return "OverDue";
  }
  return "Not due yet";
};
